import React, { useState, useMemo, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import {
  getEmergencyRequests,
  approveEmergencyRequest,
  denyEmergencyRequest,
  revokeEmergencyRequest,
  checkExpiredRequests,
  EMERGENCY_TYPES,
  REQUEST_STATUS,
} from '../../services/docuvaultEmergencyService';

const {
  FiAlertTriangle, FiClock, FiCheck, FiX, FiUser, FiMail,
  FiShield, FiFileText, FiUnlock, FiRefreshCw, FiChevronDown,
  FiChevronUp, FiActivity, FiLock, FiInbox,
} = FiIcons;

const EMERGENCY_ICONS = {
  death: FiActivity,
  medical: FiActivity,
  legal: FiShield,
  incapacitation: FiAlertTriangle,
  other: FiAlertTriangle,
};

function formatRelative(ts) {
  const diff = Date.now() - new Date(ts).getTime();
  if (diff < 60000) return 'just now';
  if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
  if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
  return `${Math.floor(diff / 86400000)}d ago`;
}

function formatExpiry(expiresAt) {
  if (!expiresAt) return null;
  const remaining = new Date(expiresAt).getTime() - Date.now();
  if (remaining <= 0) return 'Expired';
  const hours = Math.floor(remaining / 3600000);
  const mins = Math.floor((remaining % 3600000) / 60000);
  return `${hours}h ${mins}m remaining`;
}

// ── Single Request Card ─────────────────────────────────────────────────────
function RequestCard({ req, onApprove, onDeny, onRevoke }) {
  const [expanded, setExpanded] = useState(false);
  const [reviewNotes, setReviewNotes] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [showApprove, setShowApprove] = useState(false);

  const typeCfg = EMERGENCY_TYPES[req.emergencyType] || EMERGENCY_TYPES.other;
  const statusCfg = REQUEST_STATUS[req.status] || REQUEST_STATUS.pending;
  const Icon = EMERGENCY_ICONS[req.emergencyType] || FiAlertTriangle;
  const isPending = req.status === 'pending';
  const isActive = req.status === 'approved' && req.expiresAt && new Date(req.expiresAt) > new Date();

  const handleApprove = async () => {
    setActionLoading(true);
    await onApprove(req.id, reviewNotes);
    setActionLoading(false);
    setShowApprove(false);
  };

  const handleDeny = async () => {
    setActionLoading(true);
    await onDeny(req.id, reviewNotes);
    setActionLoading(false);
  };

  const handleRevoke = async () => {
    setActionLoading(true);
    await onRevoke(req.id, 'Manually revoked by owner');
    setActionLoading(false);
  };

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, x: 30, scale: 0.95 }}
      className={`rounded-2xl border-2 overflow-hidden transition-all ${
        isPending ? 'border-amber-200 bg-amber-50/30' :
        req.status === 'approved' ? 'border-green-200 bg-green-50/30' :
        req.status === 'denied' ? 'border-red-200 bg-red-50/30' :
        'border-brand-dark bg-white'
      }`}
    >
      {/* Header */}
      <div className="p-4 flex items-start gap-3">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${typeCfg.bg} border ${typeCfg.border}`}>
          <SafeIcon icon={Icon} className={`w-5 h-5 ${typeCfg.color}`} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <p className="text-sm font-bold text-accent-navy truncate">{req.docName}</p>
            <span className={`text-[8px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full ${statusCfg.bg} ${statusCfg.color}`}>
              {statusCfg.label}
            </span>
          </div>
          <div className="flex items-center gap-3 text-[10px] text-text-muted flex-wrap">
            <span className="font-bold uppercase tracking-widest">{typeCfg.label}</span>
            <span className="flex items-center gap-1">
              <SafeIcon icon={FiUser} className="w-3 h-3" />
              {req.requester.name}
            </span>
            <span>{formatRelative(req.createdAt)}</span>
            {isActive && formatExpiry(req.expiresAt) && (
              <span className="text-green-600 font-bold flex items-center gap-1">
                <SafeIcon icon={FiClock} className="w-3 h-3" />
                {formatExpiry(req.expiresAt)}
              </span>
            )}
          </div>
        </div>
        <button
          onClick={() => setExpanded(!expanded)}
          className="p-2 text-text-muted hover:text-accent-navy rounded-lg border border-brand-dark hover:border-accent-navy transition-all flex-shrink-0"
        >
          <SafeIcon icon={expanded ? FiChevronUp : FiChevronDown} className="w-4 h-4" />
        </button>
      </div>

      {/* Expanded details */}
      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden border-t border-brand-light"
          >
            <div className="p-4 space-y-3 bg-white">
              {/* Requester info */}
              <div className="grid grid-cols-2 gap-3 text-[10px]">
                <div>
                  <p className="font-black uppercase tracking-widest text-text-muted">Requester Email</p>
                  <p className="font-bold text-accent-navy flex items-center gap-1 mt-0.5">
                    <SafeIcon icon={FiMail} className="w-3 h-3" />
                    {req.requester.email}
                  </p>
                </div>
                <div>
                  <p className="font-black uppercase tracking-widest text-text-muted">Role / Relationship</p>
                  <p className="font-bold text-accent-navy mt-0.5 capitalize">
                    {req.requester.role}
                    {req.requester.relationship ? ` · ${req.requester.relationship}` : ''}
                  </p>
                </div>
              </div>

              {/* Description */}
              <div>
                <p className="font-black uppercase tracking-widest text-text-muted text-[10px] mb-1">Emergency Description</p>
                <p className="text-xs text-accent-navy font-light leading-relaxed">{req.description}</p>
              </div>

              {/* Supporting evidence */}
              {req.supportingEvidence && (
                <div>
                  <p className="font-black uppercase tracking-widest text-text-muted text-[10px] mb-1">Supporting Evidence</p>
                  <p className="text-xs text-accent-navy font-light">{req.supportingEvidence}</p>
                </div>
              )}

              {/* Signature */}
              <div className="p-3 bg-amber-50/50 rounded-xl border border-accent-gold/20">
                <p className="font-black uppercase tracking-widest text-text-muted text-[9px] mb-1">Digital Signature</p>
                <p
                  className="text-lg text-accent-navy"
                  style={{ fontFamily: "'Dancing Script', 'Brush Script MT', cursive" }}
                >
                  {req.typedSignature}
                </p>
              </div>

              {/* Timeline */}
              {req.timeline.length > 0 && (
                <div>
                  <p className="font-black uppercase tracking-widest text-text-muted text-[10px] mb-1.5">Timeline</p>
                  <div className="space-y-1">
                    {req.timeline.map((t, i) => (
                      <div key={i} className="flex items-start gap-2 text-[10px]">
                        <div className="w-1.5 h-1.5 rounded-full bg-accent-gold mt-1 flex-shrink-0" />
                        <div>
                          <span className="font-bold text-accent-navy capitalize">{t.event.replace(/_/g, ' ')}</span>
                          <span className="text-text-muted"> — {t.detail}</span>
                          <span className="text-text-muted/60 block">{formatRelative(t.timestamp)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Reviewer notes */}
              {req.reviewerNotes && (
                <div className="p-2 bg-brand-light rounded-lg">
                  <p className="font-black uppercase tracking-widest text-text-muted text-[9px] mb-0.5">Reviewer Notes</p>
                  <p className="text-[10px] text-accent-navy">{req.reviewerNotes}</p>
                </div>
              )}

              {/* Actions */}
              {isPending && !showApprove && (
                <div className="flex gap-2 pt-2">
                  <button
                    onClick={() => setShowApprove(true)}
                    className="flex-1 flex items-center justify-center gap-1.5 py-2.5 bg-green-500 text-white rounded-xl text-[9px] font-black uppercase tracking-widest hover:bg-green-600 transition-all"
                  >
                    <SafeIcon icon={FiCheck} className="w-3.5 h-3.5" /> Approve Release
                  </button>
                  <button
                    onClick={handleDeny}
                    disabled={actionLoading}
                    className="flex-1 flex items-center justify-center gap-1.5 py-2.5 bg-red-50 border-2 border-red-200 text-red-600 rounded-xl text-[9px] font-black uppercase tracking-widest hover:bg-red-100 transition-all disabled:opacity-50"
                  >
                    <SafeIcon icon={FiX} className="w-3.5 h-3.5" /> Deny
                  </button>
                </div>
              )}

              {/* Approve form */}
              {isPending && showApprove && (
                <div className="space-y-2 pt-2">
                  <input
                    value={reviewNotes}
                    onChange={e => setReviewNotes(e.target.value)}
                    placeholder="Reviewer notes (optional) — e.g. access granted for 72 hours"
                    className="w-full px-3 py-2 bg-brand-light border-2 border-brand-dark rounded-xl outline-none focus:border-accent-gold text-xs"
                  />
                  <div className="flex gap-2">
                    <button
                      onClick={handleApprove}
                      disabled={actionLoading}
                      className="flex-1 flex items-center justify-center gap-1.5 py-2.5 bg-green-500 text-white rounded-xl text-[9px] font-black uppercase tracking-widest hover:bg-green-600 transition-all disabled:opacity-50"
                    >
                      {actionLoading
                        ? <div className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        : <><SafeIcon icon={FiUnlock} className="w-3.5 h-3.5" /> Confirm Release</>
                      }
                    </button>
                    <button
                      onClick={() => setShowApprove(false)}
                      className="px-4 py-2.5 border-2 border-brand-dark text-text-muted rounded-xl text-[9px] font-black uppercase tracking-widest hover:text-accent-navy transition-all"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {/* Deny form */}
              {isPending && !showApprove && (
                <input
                  value={reviewNotes}
                  onChange={e => setReviewNotes(e.target.value)}
                  placeholder="Reason for denial (optional)"
                  className="w-full px-3 py-2 bg-brand-light border-2 border-brand-dark rounded-xl outline-none focus:border-accent-gold text-xs"
                />
              )}

              {/* Revoke active release */}
              {isActive && (
                <button
                  onClick={handleRevoke}
                  disabled={actionLoading}
                  className="w-full flex items-center justify-center gap-1.5 py-2.5 bg-red-50 border-2 border-red-200 text-red-600 rounded-xl text-[9px] font-black uppercase tracking-widest hover:bg-red-100 transition-all disabled:opacity-50"
                >
                  <SafeIcon icon={FiLock} className="w-3.5 h-3.5" /> Revoke Active Release
                </button>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

// ── Main Panel ───────────────────────────────────────────────────────────────
const DocuVaultEmergencyReviewPanel = ({ onRefresh }) => {
  const [requests, setRequests] = useState([]);
  const [filter, setFilter] = useState('pending');
  const [tick, setTick] = useState(0);

  const load = () => {
    checkExpiredRequests();
    setRequests(getEmergencyRequests());
  };

  useEffect(() => {
    load();
    const interval = setInterval(() => {
      checkExpiredRequests();
      setTick(t => t + 1);
    }, 60000); // check for expired requests every minute
    return () => clearInterval(interval);
  }, []);

  useEffect(() => { void tick; load(); }, [tick]);

  const stats = useMemo(() => {
    return {
      total: requests.length,
      pending: requests.filter(r => r.status === 'pending').length,
      approved: requests.filter(r => r.status === 'approved').length,
      denied: requests.filter(r => r.status === 'denied').length,
      active: requests.filter(r => r.status === 'approved' && r.expiresAt && new Date(r.expiresAt) > new Date()).length,
    };
  }, [requests]);

  const filtered = useMemo(() => {
    if (filter === 'all') return requests;
    if (filter === 'active') return requests.filter(r => r.status === 'approved' && r.expiresAt && new Date(r.expiresAt) > new Date());
    return requests.filter(r => r.status === filter);
  }, [requests, filter]);

  const handleApprove = async (id, notes) => {
    await approveEmergencyRequest(id, notes, 72);
    load();
    onRefresh?.();
  };

  const handleDeny = async (id, notes) => {
    denyEmergencyRequest(id, notes);
    load();
    onRefresh?.();
  };

  const handleRevoke = async (id, reason) => {
    revokeEmergencyRequest(id, reason);
    load();
    onRefresh?.();
  };

  if (stats.total === 0) return null;

  const hasPending = stats.pending > 0;

  return (
    <div className={`rounded-[28px] border-2 ${hasPending ? 'border-amber-300' : 'border-brand-dark'} overflow-hidden bg-white`}>
      {/* Header */}
      <div className={`px-5 py-4 flex items-center justify-between ${hasPending ? 'bg-amber-50' : 'bg-brand-light/50'}`}>
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${hasPending ? 'bg-amber-100' : 'bg-brand-light'}`}>
            <SafeIcon icon={hasPending ? FiAlertTriangle : FiInbox} className={`w-5 h-5 ${hasPending ? 'text-amber-600' : 'text-text-muted'}`} />
          </div>
          <div>
            <h3 className="text-sm font-black text-accent-navy">
              {stats.pending > 0
                ? `${stats.pending} Emergency Release Request${stats.pending !== 1 ? 's' : ''} Pending`
                : 'Emergency Release Requests'}
            </h3>
            <p className="text-[10px] text-text-muted font-medium">
              {stats.total} total · {stats.pending} pending · {stats.active} active · {stats.denied} denied
            </p>
          </div>
        </div>
        <button
          onClick={load}
          className="p-2 text-text-muted hover:text-accent-navy rounded-lg border border-brand-dark hover:border-accent-navy transition-all"
        >
          <SafeIcon icon={FiRefreshCw} className="w-4 h-4" />
        </button>
      </div>

      {/* Filter tabs */}
      <div className="flex gap-1 px-5 py-3 border-b border-brand-light bg-brand-light/30 overflow-x-auto">
        {[
          { id: 'pending', label: 'Pending', count: stats.pending },
          { id: 'active', label: 'Active', count: stats.active },
          { id: 'approved', label: 'All Approved', count: stats.approved },
          { id: 'denied', label: 'Denied', count: stats.denied },
          { id: 'all', label: 'All', count: stats.total },
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setFilter(tab.id)}
            className={`flex-shrink-0 px-3 py-1.5 rounded-xl text-[9px] font-black uppercase tracking-widest border-2 transition-all ${
              filter === tab.id ? 'bg-accent-navy text-white border-accent-navy' : 'border-brand-dark text-text-muted hover:border-accent-navy/40'
            }`}
          >
            {tab.label} {tab.count > 0 && `(${tab.count})`}
          </button>
        ))}
      </div>

      {/* Request list */}
      <div className="p-4 space-y-3 max-h-[400px] overflow-y-auto">
        {filtered.length === 0 ? (
          <div className="text-center py-8">
            <SafeIcon icon={FiInbox} className="w-8 h-8 text-text-muted/30 mx-auto mb-2" />
            <p className="text-sm text-text-muted font-light">No requests in this category.</p>
          </div>
        ) : (
          <AnimatePresence>
            {filtered.map(req => (
              <RequestCard
                key={req.id}
                req={req}
                onApprove={handleApprove}
                onDeny={handleDeny}
                onRevoke={handleRevoke}
              />
            ))}
          </AnimatePresence>
        )}
      </div>
    </div>
  );
};

export default DocuVaultEmergencyReviewPanel;